// src/utils/errorUtils.ts
// Centralised error message extraction.
// Use this instead of the repeated `error?.response?.data?.message` pattern.

/**
 * Extracts a human-readable message from any thrown value.
 *
 * Resolution order:
 *  1. Axios response body  – error.response.data.message
 *  2. Axios response body  – error.response.data.error
 *  3. Plain Error message  – error.message
 *  4. String errors        – the string itself
 *  5. Fallback             – caller-supplied default or generic message
 */
export const extractErrorMessage = (
  error: unknown,
  fallback = 'An unexpected error occurred. Please try again.'
): string => {
  if (!error) return fallback;

  if (typeof error === 'string') return error || fallback;

  if (error instanceof Error) {
    return error.message || fallback;
  }

  // Axios or similar HTTP error
  const httpError = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };

  return (
    httpError?.response?.data?.message ||
    httpError?.response?.data?.error ||
    httpError?.message ||
    fallback
  );
};

/**
 * Formats validation error messages returned by the backend.
 * e.g. "User validation failed: Path `name` is required., Path `email` is required."
 * → "name is required.\nemail is required."
 */
export const formatValidationErrors = (message: string): string =>
  message
    .replace(/User validation failed: /g, '')
    .replace(/, /g, '\n')
    .replace(/Path `(\w+)` is required\./g, '$1 is required');
